import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  return (
    <section className="flex overflow-hidden relative justify-center items-center py-16 min-h-screen bg-gradient-to-br via-purple-900 sm:py-16 md:py-20 from-slate-900 to-slate-900">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br via-purple-900 from-slate-900 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-r animate-pulse from-red-500/10 via-blue-500/10 to-purple-500/10"></div>
        <div className="absolute inset-0 bg-gradient-to-t to-transparent from-slate-900/50"></div>
      </div>

      {/* Content Overlay */}
      <div className="container relative z-10 px-4 mx-auto max-w-full text-center">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mx-auto max-w-6xl"
        >
          <motion.h1
            variants={itemVariants}
            className="px-2 mb-4 text-3xl font-bold leading-tight text-white sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
          >
            SMS Solutions That
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-blue-500">
              Transform Business
            </span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="px-4 mx-auto mb-8 max-w-3xl text-base leading-relaxed text-gray-300 sm:mb-10 md:mb-12 sm:text-lg md:text-xl lg:text-2xl"
          >
            Powerful, reliable, and scalable messaging solutions designed to
            connect you with your audience instantly
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 gap-4 mx-auto mb-12 max-w-4xl sm:gap-6 md:gap-8 sm:mb-14 md:mb-16 lg:grid-cols-2"
          >
            {[
              {
                icon: MessageSquare,
                text: "Quick SMS",
                description: "Send instant messages to individuals",
                color: "from-red-500 to-red-600",
                bgColor: "bg-red-500/10",
                borderColor: "border-red-500/30",
              },
              {
                icon: Send,
                text: "Bulk SMS",
                description: "Reach thousands with targeted campaigns",
                color: "from-blue-500 to-indigo-600",
                bgColor: "bg-blue-500/10",
                borderColor: "border-blue-500/30",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={iconVariants}
                whileHover="hover"
                className="relative group"
              >
                <div
                  className={`relative p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl ${item.bgColor} border ${item.borderColor} backdrop-blur-sm shadow-xl transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl overflow-hidden`}
                >
                  {/* Background gradient overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                  ></div>

                  {/* Icon container */}
                  <div className="flex relative z-10 items-center mb-4 sm:mb-6">
                    <div
                      className={`p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br ${item.color} shadow-lg`}
                    >
                      <item.icon className="w-6 h-6 text-white sm:w-7 sm:h-7 md:w-8 md:h-8" />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <h3 className="text-lg font-bold text-white sm:text-xl md:text-2xl">
                        {item.text}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="relative z-10 text-sm leading-relaxed text-gray-300 sm:text-base md:text-lg">
                    {item.description}
                  </p>

                  {/* Hover effect line */}
                  <div
                    className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${item.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}
                  ></div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex flex-col gap-3 justify-center items-center px-4 sm:gap-4 sm:flex-row"
          >
            <motion.button
              onClick={() => navigate("/login")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 w-full text-base font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-full shadow-lg transition-all duration-300 sm:w-auto sm:px-8 sm:py-4 sm:text-lg hover:shadow-xl"
            >
              Get Started Free
            </motion.button>
            <motion.button
              onClick={() => navigate("/contact-us")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 w-full text-base font-semibold text-white rounded-full border-2 border-white transition-all duration-300 sm:w-auto sm:px-8 sm:py-4 sm:text-lg hover:bg-white hover:text-gray-900"
            >
              Contact Us
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating SMS Icons */}
      <div className="overflow-hidden absolute inset-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-white/10"
            style={{
              left: `${Math.random() * 80 + 10}%`,
              top: `${Math.random() * 80 + 10}%`,
            }}
            animate={{
              y: [0, -50, 0],
              x: [0, Math.random() * 20 - 10, 0],
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut",
            }}
          >
            <MessageSquare
              size={20 + Math.random() * 16}
              className="hidden sm:block"
            />
            <MessageSquare
              size={16 + Math.random() * 8}
              className="block sm:hidden"
            />
          </motion.div>
        ))}

        {/* Additional floating elements */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`element-${i}`}
            className="absolute text-white/5"
            style={{
              left: `${Math.random() * 80 + 10}%`,
              top: `${Math.random() * 80 + 10}%`,
            }}
            animate={{
              y: [0, -30, 0],
              rotate: [0, -360],
              scale: [0.8, 1.1, 0.8],
            }}
            transition={{
              duration: 6 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          >
            <Send size={16 + Math.random() * 12} className="hidden sm:block" />
            <Send size={12 + Math.random() * 8} className="block sm:hidden" />
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
